import { FC } from "react";
import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  createStyles,
  FormControlLabel,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { every, orderBy, some, uniq } from "lodash";

import {
  useAssetKYWCredentialSubmissions,
  useAssetKYWRoyaltyAccounts,
} from "services/assets";
import { useOtherSourceSubmissions } from "services/otherSourceSubmissions";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: theme.spacing(2),
    },
    removeBoxShadow: {
      boxShadow: "none",
    },
    header: {
      fontSize: "1.8rem",
      fontWeight: theme.typography.fontWeightMedium,
      textTransform: "unset",
    },
    italicCaption: {
      color: theme.custom.palette.gray.darker,
      fontSize: "1rem",
      fontStyle: "italic",
    },
    checked: {
      "& .MuiCheckbox-colorSecondary.Mui-disabled": {
        color: theme.custom.palette.green.main,
      },
      "& .MuiFormControlLabel-label.Mui-disabled": {
        color: theme.custom.palette.green.main,
      },
    },
  }),
);

const CheckboxWithLabel: FC<{
  className: string;
  isChecked: boolean;
  isLoading: boolean;
  label: string;
}> = ({ className, isChecked, isLoading, label }) => {
  return (
    <FormControlLabel
      className={className}
      disabled
      control={
        isLoading ? (
          <Box
            height="42px"
            width="42px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ opacity: 0.38 }} // disabledOpacity
          >
            <CircularProgress size={12} />
          </Box>
        ) : (
          <Checkbox checked={isChecked} name={label} />
        )
      }
      label={label}
    />
  );
};

interface AddedRoyaltyAccountsChecklistInfo {
  isFetching: boolean;
  isLoading: boolean;
  isAnyAccountConnected: boolean;
  isChecklistComplete: boolean;
  isPerformingRightsOrganizationConnected: boolean;
  isPublisherConnected: boolean;
  isLabelDistributorConnected: boolean;
  isOtherSourceConnected: boolean;
  lastAddedSource: {
    royaltyPayorName: string;
    royaltyAccountUsername: string;
  };
}
export const useAddedAccountsChecklistInfo = (): AddedRoyaltyAccountsChecklistInfo => {
  const otherSourceSubmissionsQueries = useOtherSourceSubmissions();
  const assetKYWRoyaltyAccountsQueries = useAssetKYWRoyaltyAccounts();
  const assetKYWRoyaltyAccounts = assetKYWRoyaltyAccountsQueries.data || [];

  const assetKYWCredentialSubmissionQueries = useAssetKYWCredentialSubmissions();
  const assetKYWCredentialSubmission =
    assetKYWCredentialSubmissionQueries.data || [];

  const assets = [...assetKYWRoyaltyAccounts, ...assetKYWCredentialSubmission];

  const sortedAssets = orderBy(
    assets || [],
    [asset => asset.created_at],
    ["desc"],
  );
  const connectedPayorKinds = sortedAssets.map(asset => {
    const royaltyPayor =
      "credential_submission" in asset
        ? asset.credential_submission.royalty_payor
        : asset.royalty_account.royalty_payor;
    const isGetCredentials = some(royaltyPayor?.permissions, {
      codename: "get_credentials",
    });
    const isCredentialSubmissionComplete =
      "credential_submission" in asset &&
      !!asset?.credential_submission.attested_ltm;
    if (isGetCredentials && !isCredentialSubmissionComplete) {
      return;
    }
    return royaltyPayor?.kind;
  });

  const sortedOtherSourceSubmissions = orderBy(
    otherSourceSubmissionsQueries.data || [],
    [oss => oss.created_at],
    ["desc"],
  );
  const otherSourcePayorKinds =
    sortedOtherSourceSubmissions.map(
      otherSource => otherSource.royalty_payor_kind,
    ) || [];

  const allPayorKinds = uniq([
    ...connectedPayorKinds,
    ...otherSourcePayorKinds,
  ]);

  const isPerformingRightsOrganizationConnected = allPayorKinds.includes("pro");
  const isPublisherConnected = allPayorKinds.includes("publisher");
  const isLabelDistributorConnected = allPayorKinds.some(kind =>
    ["digital_distributor", "label"].includes(kind),
  );

  const lastAddedOtherSourceSubmission = sortedOtherSourceSubmissions[0];
  const lastAddedAsset = sortedAssets[0];
  const lastAddedSource =
    (lastAddedAsset?.created_at ?? "0") >
    (lastAddedOtherSourceSubmission?.created_at ?? "0")
      ? !!lastAddedAsset && "royalty_account" in lastAddedAsset
        ? lastAddedAsset.royalty_account.royalty_payor.name
        : !!lastAddedAsset && "credential_submission" in lastAddedAsset
        ? lastAddedAsset.credential_submission.royalty_payor.name
        : ""
      : lastAddedOtherSourceSubmission?.royalty_payor_name;

  const isChecklistComplete = every([
    isPerformingRightsOrganizationConnected,
    isPublisherConnected,
    isLabelDistributorConnected,
  ]);

  return {
    isFetching:
      otherSourceSubmissionsQueries.isFetching ||
      assetKYWRoyaltyAccountsQueries.isFetching ||
      assetKYWCredentialSubmissionQueries.isFetching,
    isLoading:
      otherSourceSubmissionsQueries.isLoading ||
      assetKYWRoyaltyAccountsQueries.isLoading ||
      assetKYWCredentialSubmissionQueries.isLoading,
    isAnyAccountConnected:
      !!sortedAssets.length || !!sortedOtherSourceSubmissions.length,
    isChecklistComplete,
    isPerformingRightsOrganizationConnected,
    isPublisherConnected,
    isLabelDistributorConnected,
    isOtherSourceConnected: allPayorKinds.some(kind =>
      ["custom", "other"].includes(kind),
    ),
    lastAddedSource: {
      royaltyPayorName: lastAddedSource || "",
      royaltyAccountUsername:
        !!lastAddedAsset && "royalty_account" in lastAddedAsset
          ? lastAddedAsset.royalty_account.username
          : !!lastAddedAsset && "credential_submission" in lastAddedAsset
          ? lastAddedAsset.credential_submission.username
          : "",
    },
  };
};

const AddedRoyaltyAccountsChecklist: FC<{
  removeBoxShadow?: boolean;
}> = ({ removeBoxShadow }) => {
  const classes = useStyles();
  const {
    isFetching,
    isAnyAccountConnected,
    isPerformingRightsOrganizationConnected,
    isPublisherConnected,
    isLabelDistributorConnected,
    isOtherSourceConnected,
  } = useAddedAccountsChecklistInfo();
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <Card
      className={removeBoxShadow ? classes.removeBoxShadow : classes.container}
    >
      <Typography variant="h3" className={`${classes.header}`} gutterBottom>
        Royalty Account Checklist
      </Typography>
      <Typography variant="caption" className={classes.italicCaption}>
        {isAnyAccountConnected
          ? "These are the royalty account types you have added:"
          : "The checklist will update once accounts are added"}
      </Typography>
      <Box display="flex" flexDirection="column">
        <CheckboxWithLabel
          className={
            isPerformingRightsOrganizationConnected ? classes.checked : ""
          }
          isChecked={isPerformingRightsOrganizationConnected}
          isLoading={!isPerformingRightsOrganizationConnected && isFetching}
          label={
            isXsDown
              ? "Performing Rights Org."
              : "Performing Rights Organization"
          }
        />
        <CheckboxWithLabel
          className={isPublisherConnected ? classes.checked : ""}
          isChecked={isPublisherConnected}
          isLoading={!isPublisherConnected && isFetching}
          label="Publisher"
        />
        <CheckboxWithLabel
          className={isLabelDistributorConnected ? classes.checked : ""}
          isChecked={isLabelDistributorConnected}
          isLoading={!isLabelDistributorConnected && isFetching}
          label="Label/Digital Distributor"
        />
        {isOtherSourceConnected && (
          <CheckboxWithLabel
            className={isOtherSourceConnected ? classes.checked : ""}
            isChecked={isOtherSourceConnected}
            isLoading={!isOtherSourceConnected && isFetching}
            label="Other"
          />
        )}
      </Box>
    </Card>
  );
};

export default AddedRoyaltyAccountsChecklist;
